import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Download, Maximize2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { preprocessMathTags, extractMathExpressions } from '../utils/mathUtils';
import { InlineMath } from './MathRenderer';

const renderInlineMathSegments = (text: string, keyPrefix: string): React.ReactNode[] => {
  if (!text) {
    return [];
  }

  const normalized = preprocessMathTags(text);
  const expressions = extractMathExpressions(normalized);

  if (!expressions.length) {
    return [<React.Fragment key={`${keyPrefix}-text-0`}>{normalized}</React.Fragment>];
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  expressions.forEach((expr, idx) => {
    if (expr.start > cursor) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-text-${idx}`}>
          {normalized.slice(cursor, expr.start)}
        </React.Fragment>
      );
    }
    const sanitizedExpression = expr.expression.replace(/\\\\/g, '\\');
    nodes.push(
      <InlineMath key={`${keyPrefix}-math-${idx}`}>
        {sanitizedExpression}
      </InlineMath>
    );
    cursor = expr.end;
  });

  if (cursor < normalized.length) {
    nodes.push(
      <React.Fragment key={`${keyPrefix}-text-tail`}>
        {normalized.slice(cursor)}
      </React.Fragment>
    );
  }

  return nodes;
};

export interface GraphConfig {
  type: 'function' | 'scatter' | 'bar' | 'line' | 'timeline';
  title?: string;
  grid?: boolean;
  color?: string;
  width?: number;
  height?: number;
  xLabel?: string;
  yLabel?: string;
  xRange?: [number, number];
  yRange?: [number, number];
  description?: string;
}

export interface FunctionGraphConfig extends GraphConfig {
  type: 'function';
  expression?: string; // Keep for backward compatibility
  variable?: string;
  // New format from graph prompt
  expressions?: string[]; // Array of expression strings
  labels?: string[]; // Labels for each expression
  colors?: string[]; // Colors for each expression
  markers?: Array<{
    x: number;
    label: string;
  }>; // X-axis markers
  intersections?: boolean; // Highlight intersection points
  showDiagonal?: boolean; // Show 45-degree line for economics
  // Legacy format support
  expression2?: string;
  expression3?: string;
  color2?: string;
  color3?: string;
  // Support for additional visual elements
  points?: [number, number][];
  lines?: Array<{
    type: 'vertical' | 'horizontal' | 'line';
    value?: number; // For vertical/horizontal lines
    points?: [number, number][]; // For arbitrary lines
    color?: string;
    style?: 'solid' | 'dashed' | 'dotted';
  }>;
}

export interface ScatterDatasetConfig {
  label?: string;
  color?: string;
  points: [number, number][];
}

export interface ScatterGraphConfig extends GraphConfig {
  type: 'scatter';
  points?: [number, number][];
  datasets?: ScatterDatasetConfig[];
  trend?: boolean;
}

export interface BarGraphConfig extends GraphConfig {
  type: 'bar';
  data: Record<string, number>;
}

export interface LineGraphConfig extends GraphConfig {
  type: 'line';
  data: [number, number][];
}

export interface TimelineGraphConfig extends GraphConfig {
  type: 'timeline';
  events?: Array<{
    time: number;
    label: string;
    type?: 'start' | 'milestone' | 'end' | 'event';
    description?: string;
  }>;
  periods?: Array<{
    start: number;
    end: number;
    label: string;
    color?: string;
    description?: string;
  }>;
  markers?: Array<{
    x: number;
    label: string;
  }>;
  unit?: string; // e.g., "חודשים", "שנים", "ימים"
}

type GraphRenderConfig = FunctionGraphConfig | ScatterGraphConfig | BarGraphConfig | LineGraphConfig | TimelineGraphConfig;

// Legacy client-side renderer for ```graph:*``` blocks. New graphs arrive as markdown images from the backend.
// StudyBel Color Palette
const STUDYBEL_COLORS = {
  primary: '#3B82F6',    // StudyBel Blue
  purple: '#8B5CF6',     // StudyBel Purple  
  red: '#EF4444',        // StudyBel Red
  black: '#1F2937',      // StudyBel Dark
  green: '#10B981',      // StudyBel Green
  orange: '#F59E0B',     // StudyBel Orange
  indigo: '#6366F1',     // StudyBel Indigo
  pink: '#EC4899'        // StudyBel Pink
} as const;

// Get a color from the palette by index (cycles through colors)
const getStudybelColor = (index: number = 0): string => {
  const colors = Object.values(STUDYBEL_COLORS);
  return colors[index % colors.length];
};

interface GraphRendererProps {
  config: GraphRenderConfig;
  onError?: (error: string) => void;
  isRTL?: boolean;
}

const GraphRenderer: React.FC<GraphRendererProps> = ({ config, onError, isRTL = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [legendData, setLegendData] = useState<Array<{label: string, color: string}>>([]);
  const [markerLegendData, setMarkerLegendData] = useState<Array<{number: number, label: string, type: 'x-axis' | 'y-axis'}>>([]);

  // Use wider default for timeline graphs
  const isTimeline = config.type === 'timeline';
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  
  // Get container width for responsive timelines
  useEffect(() => {
    if (isTimeline && containerRef.current) {
      const updateWidth = () => {
        const width = containerRef.current?.offsetWidth || 800;
        setContainerWidth(Math.max(width - 32, 600)); // Subtract padding, minimum 600px
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [isTimeline]);
  
  const defaultWidth = config.width || (isTimeline ? containerWidth : 400);
  const defaultHeight = config.height || (isTimeline ? 350 : 300);
  
  // Calculate actual canvas dimensions based on fullscreen state
  const width = isFullscreen ? Math.min(window.innerWidth * 0.9, 1200) : defaultWidth;
  const height = isFullscreen ? Math.min(window.innerHeight * 0.8, 800) : defaultHeight;
  const padding = isFullscreen ? 80 : (isTimeline ? 100 : 60); // More padding for timelines

  useEffect(() => {
    if (!canvasRef.current) return;

    try {
      renderGraph();
      setError(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown rendering error';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [config, isFullscreen, containerWidth]); // Re-render when fullscreen toggles or container width changes

  const renderGraph = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set up canvas styling
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw axes and grid (skip for timeline)
    if (config.type !== 'timeline') {
      drawAxesAndGrid(ctx);
    }
    
    // Draw graph based on type
    switch (config.type) {
      case 'function':
        drawFunctionGraph(ctx, config as FunctionGraphConfig);
        break;
      case 'scatter':
        drawScatterGraph(ctx, config as ScatterGraphConfig);
        break;
      case 'bar':
        drawBarGraph(ctx, config as BarGraphConfig);
        break;
      case 'line':
        drawLineGraph(ctx, config as LineGraphConfig);
        break;
      case 'timeline':
        drawTimelineGraph(ctx, config as TimelineGraphConfig);
        break;
    }
    
  };

  const drawAxesAndGrid = (ctx: CanvasRenderingContext2D) => {
    const xRange = config.xRange || [-10, 10];
    const yRange = config.yRange || [-10, 10];
    
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    
    // Grid lines
    if (config.grid) {
      const xStep = (xRange[1] - xRange[0]) / 10;
      const yStep = (yRange[1] - yRange[0]) / 10;
      
      for (let i = 0; i <= 10; i++) {
        // Vertical grid lines
        const x = padding + (i / 10) * (width - 2 * padding);
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, height - padding);
        ctx.stroke();
        
        // Horizontal grid lines
        const y = padding + (i / 10) * (height - 2 * padding);
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
      }
    }
    
    // Main axes
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    
    // X-axis
    const zeroY = padding + (yRange[1] / (yRange[1] - yRange[0])) * (height - 2 * padding);
    if (zeroY >= padding && zeroY <= height - padding) {
      ctx.beginPath();
      ctx.moveTo(padding, zeroY);
      ctx.lineTo(width - padding, zeroY);
      ctx.stroke();
    }
    
    // Y-axis
    const zeroX = padding + (-xRange[0] / (xRange[1] - xRange[0])) * (width - 2 * padding);
    if (zeroX >= padding && zeroX <= width - padding) {
      ctx.beginPath();
      ctx.moveTo(zeroX, padding);
      ctx.lineTo(zeroX, height - padding);
      ctx.stroke();
    }
    
    // Axis labels (tick marks)
    ctx.fillStyle = '#374151';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    // X-axis tick labels
    for (let i = 0; i <= 10; i++) {
      const x = padding + (i / 10) * (width - 2 * padding);
      const value = xRange[0] + (i / 10) * (xRange[1] - xRange[0]);
      if (Math.abs(value) > 0.01 || value === 0) {
        ctx.fillText(value.toFixed(1), x, height - padding + 15);
      }
    }
    
    // Y-axis tick labels
    ctx.textAlign = 'right';
    for (let i = 0; i <= 10; i++) {
      const y = padding + (i / 10) * (height - 2 * padding);
      const value = yRange[1] - (i / 10) * (yRange[1] - yRange[0]);
      if (Math.abs(value) > 0.01 || value === 0) {
        ctx.fillText(value.toFixed(1), padding - 10, y + 4);
      }
    }
    
    // Draw axis variable names
    if (config.xLabel || config.yLabel) {
      ctx.fillStyle = '#374151';
      ctx.font = 'bold 14px Arial';
      
      // X-axis label (bottom center)
      if (config.xLabel) {
        ctx.textAlign = 'center';
        ctx.fillText(config.xLabel, width / 2, height - 10);
      }
      
      // Y-axis label (left side, rotated)
      if (config.yLabel) {
        ctx.save();
        ctx.translate(15, height / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'center';
        ctx.fillText(config.yLabel, 0, 0);
        ctx.restore();
      }
    }
  };

  const drawFunctionGraph = (ctx: CanvasRenderingContext2D, config: FunctionGraphConfig) => {
    const xRange = config.xRange || [-10, 10];
    const yRange = config.yRange || [-10, 10];
    
    // Collect all expressions to render
    const expressionsToRender: Array<{expression: string, color: string, label?: string}> = [];
    
    // Add primary expression (backward compatibility)
    if (config.expression) {
      expressionsToRender.push({
        expression: config.expression,
        color: config.color || STUDYBEL_COLORS.primary,
        label: config.labels?.[0] || 'f(x)'
      });
    }
    
    // Add new format expressions
    if (config.expressions && config.expressions.length > 0) {
      config.expressions.forEach((expr, index) => {
        expressionsToRender.push({
          expression: expr,
          color: config.colors?.[index] || getStudybelColor(index + (config.expression ? 1 : 0)),
          label: config.labels?.[index] || `f${index + 1}(x)`
        });
      });
    }
    
    // Add legacy format expressions (expression2, expression3, etc.)
    if (config.expression2) {
      expressionsToRender.push({
        expression: config.expression2,
        color: config.color2 || getStudybelColor(1),
        label: 'f2(x)'
      });
    }
    if (config.expression3) {
      expressionsToRender.push({
        expression: config.expression3,
        color: config.color3 || getStudybelColor(2),
        label: 'f3(x)'
      });
    }
    
    // Show diagonal line for economics graphs
    if (config.showDiagonal) {
      ctx.strokeStyle = '#6b7280';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      
      const diagonalStart = Math.max(xRange[0], yRange[0]);
      const diagonalEnd = Math.min(xRange[1], yRange[1]);
      
      const startX = padding + ((diagonalStart - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
      const startY = padding + ((yRange[1] - diagonalStart) / (yRange[1] - yRange[0])) * (height - 2 * padding);
      const endX = padding + ((diagonalEnd - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
      const endY = padding + ((yRange[1] - diagonalEnd) / (yRange[1] - yRange[0])) * (height - 2 * padding);
      
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    
    // Store functions and their data for intersection calculation
    const functions: Array<{func: (x: number) => number, color: string, label: string}> = [];
    
    // Render each expression
    expressionsToRender.forEach(({ expression, color, label }) => {
      try {
        // Check if this is a function of y (x = h(y)) or x (y = f(x))
        const isYFunction = /\by\b/.test(expression) && !/\bx\b/.test(expression);
        
        if (isYFunction) {
          // This is a function of y, so we need to parse it as x = h(y)
          const func = parseFunctionOfY(expression);
          
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          
          let firstPoint = true;
          const step = (yRange[1] - yRange[0]) / (height - 2 * padding);
          
          for (let y = yRange[0]; y <= yRange[1]; y += step) {
            try {
              const x = func(y);
              
              if (!isNaN(x) && isFinite(x)) {
                const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
                const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
                
                if (firstPoint) {
                  ctx.moveTo(canvasX, canvasY);
                  firstPoint = false;
                } else {
                  ctx.lineTo(canvasX, canvasY);
                }
              }
            } catch (error) {
              // Skip invalid points
            }
          }
          
          ctx.stroke();
          
          // For y-functions, we can't easily add to functions array for intersections
          // since the interface expects (x: number) => number, not (y: number) => number
        } else {
          // This is a standard function of x
          const func = parseFunction(expression);
          
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          
          let firstPoint = true;
          const step = (xRange[1] - xRange[0]) / (width - 2 * padding);
          
          for (let x = xRange[0]; x <= xRange[1]; x += step) {
            try {
              const y = func(x);
              
              if (!isNaN(y) && isFinite(y)) {
                const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
                const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
                
                if (firstPoint) {
                  ctx.moveTo(canvasX, canvasY);
                  firstPoint = false;
                } else {
                  ctx.lineTo(canvasX, canvasY);
                }
              }
            } catch (error) {
              // Skip invalid points
            }
          }
          
          ctx.stroke();
          
          // Add to functions array for intersections and labels
          functions.push({ func, color, label: label || '' });
        }
      } catch (error) {
        console.error('Function parsing error:', error);
        if (onError) {
          onError(`Function parsing error for expression "${expression}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
    });
    
    // Highlight intersections if requested (only for x-functions)
    if (config.intersections && functions.length > 1) {
      drawIntersections(ctx, functions, xRange, yRange);
    }
    
    // Draw x-axis markers and collect legend data
    if (config.markers && config.markers.length > 0) {
      const markerLegend: Array<{number: number, label: string, type: 'x-axis' | 'y-axis'}> = [];
      drawXAxisMarkers(ctx, config.markers, xRange, yRange, markerLegend);
      setMarkerLegendData(markerLegend);
    } else {
      setMarkerLegendData([]);
    }
    
    // Collect legend data for HTML rendering
    const legendInfo = expressionsToRender.map((expr, index) => ({
      label: config.labels?.[index] || `f${index + 1}(x)`,
      color: expr.color
    }));
    setLegendData(legendInfo);
    
    // Render additional points
    if (config.points && config.points.length > 0) {
      ctx.fillStyle = config.color || STUDYBEL_COLORS.primary;
      
      config.points.forEach(([x, y]) => {
        const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
        const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
        
        ctx.beginPath();
        ctx.arc(canvasX, canvasY, 4, 0, 2 * Math.PI);
        ctx.fill();
      });
    }
    
    // Render additional lines
    if (config.lines && config.lines.length > 0) {
      config.lines.forEach((line, index) => {
        ctx.strokeStyle = line.color || getStudybelColor(index + 2);
        ctx.lineWidth = 1;
        
        // Set line style
        if (line.style === 'dashed') {
          ctx.setLineDash([5, 5]);
        } else if (line.style === 'dotted') {
          ctx.setLineDash([2, 2]);
        } else {
          ctx.setLineDash([]);
        }
        
        ctx.beginPath();
        
        if (line.type === 'vertical' && line.value !== undefined) {
          const canvasX = padding + ((line.value - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
          ctx.moveTo(canvasX, padding);
          ctx.lineTo(canvasX, height - padding);
        } else if (line.type === 'horizontal' && line.value !== undefined) {
          const canvasY = padding + ((yRange[1] - line.value) / (yRange[1] - yRange[0])) * (height - 2 * padding);
          ctx.moveTo(padding, canvasY);
          ctx.lineTo(width - padding, canvasY);
        } else if (line.type === 'line' && line.points && line.points.length >= 2) {
          line.points.forEach(([x, y], pointIndex) => {
            const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
            const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
            
            if (pointIndex === 0) {
              ctx.moveTo(canvasX, canvasY);
            } else {
              ctx.lineTo(canvasX, canvasY);
            }
          });
        }
        
        ctx.stroke();
        ctx.setLineDash([]); // Reset line dash
      });
    }
  };

  const drawIntersections = (
    ctx: CanvasRenderingContext2D,
    functions: Array<{func: (x: number) => number, color: string, label: string}>,
    xRange: [number, number],
    yRange: [number, number]
  ) => {
    // Find intersection points between functions
    const intersections: Array<{x: number, y: number}> = [];
    
    for (let i = 0; i < functions.length; i++) {
      for (let j = i + 1; j < functions.length; j++) {
        const func1 = functions[i].func;
        const func2 = functions[j].func;
        
        // Simple numerical method to find intersections
        const numSteps = 100;
        const stepSize = (xRange[1] - xRange[0]) / numSteps;
        
        for (let step = 0; step < numSteps; step++) {
          const x1 = xRange[0] + step * stepSize;
          const x2 = xRange[0] + (step + 1) * stepSize;
          
          try {
            const y1_func1 = func1(x1);
            const y1_func2 = func2(x1);
            const y2_func1 = func1(x2);
            const y2_func2 = func2(x2);
            
            // Check if functions cross between these points
            if (isFinite(y1_func1) && isFinite(y1_func2) && isFinite(y2_func1) && isFinite(y2_func2)) {
              const diff1 = y1_func1 - y1_func2;
              const diff2 = y2_func1 - y2_func2;
              
              if (diff1 * diff2 < 0) { // Sign change indicates intersection
                // Use bisection method to find more precise intersection
                let left = x1;
                let right = x2;
                
                for (let iter = 0; iter < 10; iter++) {
                  const mid = (left + right) / 2;
                  const midDiff = func1(mid) - func2(mid);
                  
                  if (Math.abs(midDiff) < 0.01) {
                    const y = func1(mid);
                    if (y >= yRange[0] && y <= yRange[1]) {
                      intersections.push({x: mid, y});
                    }
                    break;
                  }
                  
                  if (midDiff * diff1 < 0) {
                    right = mid;
                  } else {
                    left = mid;
                  }
                }
              }
            }
          } catch (e) {
            // Skip invalid points
            continue;
          }
        }
      }
    }
    
    // Draw intersection points
    ctx.fillStyle = '#dc2626';
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 2;
    
    intersections.forEach(({x, y}) => {
      const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
      const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
      
      // Draw intersection point
      ctx.beginPath();
      ctx.arc(canvasX, canvasY, 6, 0, 2 * Math.PI);
      ctx.fill();
      
      // Draw crosshair
      ctx.beginPath();
      ctx.moveTo(canvasX - 8, canvasY);
      ctx.lineTo(canvasX + 8, canvasY);
      ctx.moveTo(canvasX, canvasY - 8);
      ctx.lineTo(canvasX, canvasY + 8);
      ctx.stroke();
    });
  };

  const drawXAxisMarkers = (
    ctx: CanvasRenderingContext2D,
    markers: Array<{x: number, label: string}>,
    xRange: [number, number],
    yRange: [number, number],
    markerLegend: Array<{number: number, label: string, type: 'x-axis' | 'y-axis'}>
  ) => {
    ctx.strokeStyle = '#6b7280';
    ctx.fillStyle = '#374151';
    ctx.lineWidth = 1;
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    
    markers.forEach(({x, label}, index) => {
      const markerNumber = index + 1;
      
      // Check if this marker is actually for y-axis (based on label or if x is outside xRange)
      const isYAxisMarker = label.includes('ציר y') || label.includes('y-axis') || x < xRange[0] || x > xRange[1];
      
      if (isYAxisMarker) {
        // Treat the 'x' value as a y value for y-axis markers
        const y = x;
        if (y >= yRange[0] && y <= yRange[1]) {
          const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
          
          // Draw horizontal marker line
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(padding, canvasY);
          ctx.lineTo(width - padding, canvasY);
          ctx.stroke();
          ctx.setLineDash([]);
          
          // Draw numbered marker away from axis
          ctx.fillStyle = '#374151';
          ctx.textAlign = 'center';
          ctx.font = 'bold 14px Arial';
          ctx.fillText(markerNumber.toString(), padding - 35, canvasY + 5);
          
          // Add to legend
          markerLegend.push({
            number: markerNumber,
            label: label,
            type: 'y-axis'
          });
        }
      } else {
        // Standard x-axis marker
        if (x >= xRange[0] && x <= xRange[1]) {
          const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
          
          // Draw vertical marker line
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(canvasX, padding);
          ctx.lineTo(canvasX, height - padding);
          ctx.stroke();
          ctx.setLineDash([]);
          
          // Draw numbered marker away from axis
          ctx.fillStyle = '#374151';
          ctx.textAlign = 'center';
          ctx.font = 'bold 14px Arial';
          ctx.fillText(markerNumber.toString(), canvasX, height - padding + 35);
          
          // Add to legend
          markerLegend.push({
            number: markerNumber,
            label: label,
            type: 'x-axis'
          });
        }
      }
    });
  };

  // Safe mathematical expression evaluator without eval() or Function constructor
  const safeMathEval = (expression: string, variable: number, variableName: string = 'x'): number => {
    // Replace the variable with the actual value
    const variableValue = variable < 0 ? `(${variable})` : variable.toString();
    const variableRegex = new RegExp(`\\b${variableName}\\b`, 'g');
    let expr = expression.replace(variableRegex, variableValue);
    
    // Simple recursive descent parser for mathematical expressions
    let pos = 0;
    
    const peek = (): string => expr[pos] || '';
    const consume = (): string => expr[pos++] || '';
    const skipWhitespace = () => {
      while (pos < expr.length && /\s/.test(expr[pos])) pos++;
    };
    
    const parseNumber = (): number => {
      skipWhitespace();
      let start = pos;
      if (peek() === '-' || peek() === '+') pos++;
      while (pos < expr.length && (/\d/.test(expr[pos]) || expr[pos] === '.')) pos++;
      const numStr = expr.slice(start, pos);
      return parseFloat(numStr);
    };
    
    const parseFunction = (): number => {
      skipWhitespace();
      const start = pos;
      while (pos < expr.length && /[a-zA-Z.]/.test(expr[pos])) pos++;
      const funcName = expr.slice(start, pos);
      
      skipWhitespace();
      if (peek() !== '(') throw new Error(`Expected '(' after function ${funcName}`);
      consume(); // consume '('
      
      // Parse function arguments
      const args: number[] = [];
      
      // Parse first argument
      args.push(parseExpression());
      
      // Parse additional arguments for multi-argument functions
      while (peek() === ',') {
        consume(); // consume ','
        skipWhitespace();
        args.push(parseExpression());
      }
      
      skipWhitespace();
      if (peek() !== ')') throw new Error(`Expected ')' after function argument`);
      consume(); // consume ')'
      
      // Execute the function
      switch (funcName) {
        case 'Math.sin': return Math.sin(args[0]);
        case 'Math.cos': return Math.cos(args[0]);
        case 'Math.tan': return Math.tan(args[0]);
        case 'Math.log': return Math.log(args[0]);
        case 'Math.sqrt': return Math.sqrt(args[0]);
        case 'Math.abs': return Math.abs(args[0]);
        case 'Math.exp': return Math.exp(args[0]);
        case 'Math.pow': 
          if (args.length !== 2) throw new Error(`Math.pow requires exactly 2 arguments, got ${args.length}`);
          return Math.pow(args[0], args[1]);
        case 'Math.min': 
          if (args.length < 2) throw new Error(`Math.min requires at least 2 arguments, got ${args.length}`);
          return Math.min(...args);
        case 'Math.max': 
          if (args.length < 2) throw new Error(`Math.max requires at least 2 arguments, got ${args.length}`);
          return Math.max(...args);
        case 'sin': return Math.sin(args[0]);
        case 'cos': return Math.cos(args[0]);
        case 'tan': return Math.tan(args[0]);
        case 'log': return Math.log(args[0]);
        case 'sqrt': return Math.sqrt(args[0]);
        case 'abs': return Math.abs(args[0]);
        case 'exp': return Math.exp(args[0]);
        case 'pow': 
          if (args.length !== 2) throw new Error(`pow requires exactly 2 arguments, got ${args.length}`);
          return Math.pow(args[0], args[1]);
        case 'min': 
          if (args.length < 2) throw new Error(`min requires at least 2 arguments, got ${args.length}`);
          return Math.min(...args);
        case 'max': 
          if (args.length < 2) throw new Error(`max requires at least 2 arguments, got ${args.length}`);
          return Math.max(...args);
        default: throw new Error(`Unknown function: ${funcName}`);
      }
    };
    
    const parseFactor = (): number => {
      skipWhitespace();
      
      if (peek() === '(') {
        consume(); // consume '('
        const result = parseExpression();
        skipWhitespace();
        if (peek() !== ')') throw new Error(`Expected ')'`);
        consume(); // consume ')'
        return result;
      }
      
      if (peek() === '-') {
        consume(); // consume '-'
        return -parseFactor();
      }
      
      if (peek() === '+') {
        consume(); // consume '+'
        return parseFactor();
      }
      
      // Check for constants
      if (expr.slice(pos).startsWith('Math.PI')) {
        pos += 7;
        return Math.PI;
      }
      if (expr.slice(pos).startsWith('Math.E')) {
        pos += 6;
        return Math.E;
      }
      if (expr.slice(pos).startsWith('PI')) {
        pos += 2;
        return Math.PI;
      }
      if (expr.slice(pos).startsWith('E') && (pos + 1 >= expr.length || !/[a-zA-Z]/.test(expr[pos + 1]))) {
        pos += 1;
        return Math.E;
      }
      
      // Check for functions
      if (/[a-zA-Z]/.test(peek())) {
        return parseFunction();
      }
      
      // Parse number
      return parseNumber();
    };
    
    const parsePower = (): number => {
      let result = parseFactor();
      
      while (true) {
        skipWhitespace();
        if (peek() === '*' && expr[pos + 1] === '*') {
          pos += 2; // consume '**'
          const exponent = parseFactor();
          result = Math.pow(result, exponent);
        } else {
          break;
        }
      }
      
      return result;
    };
    
    const parseMultiplicationDivision = (): number => {
      let result = parsePower();
      
      while (true) {
        skipWhitespace();
        if (peek() === '*' && expr[pos + 1] !== '*') {
          consume(); // consume '*'
          result *= parsePower();
        } else if (peek() === '/') {
          consume(); // consume '/'
          const divisor = parsePower();
          if (divisor === 0) throw new Error('Division by zero');
          result /= divisor;
        } else {
          break;
        }
      }
      
      return result;
    };
    
    const parseExpression = (): number => {
      let result = parseMultiplicationDivision();
      
      while (true) {
        skipWhitespace();
        if (peek() === '+') {
          consume(); // consume '+'
          result += parseMultiplicationDivision();
        } else if (peek() === '-') {
          consume(); // consume '-'
          result -= parseMultiplicationDivision();
        } else {
          break;
        }
      }
      
      return result;
    };
    
    try {
      const result = parseExpression();
      skipWhitespace();
      if (pos < expr.length) {
        throw new Error(`Unexpected character at position ${pos}: ${expr[pos]}`);
      }
      return result;
    } catch (error) {
      throw new Error(`Cannot evaluate expression: ${expr} - ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const parseFunction = (expression: string): (x: number) => number => {
    // Simple function parser for basic expressions
    // Use a two-step approach to avoid replacing already prefixed Math functions
    let func = expression;
    
    // Step 1: Protect existing Math. functions by temporarily replacing them
    const mathFunctionMap: { [key: string]: string } = {};
    let counter = 0;
    
    func = func.replace(/Math\.\w+/g, (match) => {
      const placeholder = `__MATH_FUNC_${counter++}__`;
      mathFunctionMap[placeholder] = match;
      return placeholder;
    });
    
    // Step 2: Replace standalone function names with Math. prefixed versions
    func = func
      .replace(/\^/g, '**')
      .replace(/\bsin\b/g, 'Math.sin')
      .replace(/\bcos\b/g, 'Math.cos')
      .replace(/\btan\b/g, 'Math.tan')
      .replace(/\blog\b/g, 'Math.log')
      .replace(/\bsqrt\b/g, 'Math.sqrt')
      .replace(/\babs\b/g, 'Math.abs')
      .replace(/\bexp\b/g, 'Math.exp')
      .replace(/\bpow\b/g, 'Math.pow')
      .replace(/\bpi\b/g, 'Math.PI')
      .replace(/\be\b/g, 'Math.E');
    
    // Step 3: Restore the protected Math functions
    Object.keys(mathFunctionMap).forEach(placeholder => {
      func = func.replace(placeholder, mathFunctionMap[placeholder]);
    });
    
    return (x: number) => {
      try {
        const result = safeMathEval(func, x, 'x');
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new Error(`Invalid function expression: ${errorMessage}`);
      }
    };
  };

  const parseFunctionOfY = (expression: string): (y: number) => number => {
    // Simple function parser for expressions in terms of y
    let func = expression;
    
    // Step 1: Protect existing Math. functions by temporarily replacing them
    const mathFunctionMap: { [key: string]: string } = {};
    let counter = 0;
    
    func = func.replace(/Math\.\w+/g, (match) => {
      const placeholder = `__MATH_FUNC_${counter++}__`;
      mathFunctionMap[placeholder] = match;
      return placeholder;
    });
    
    // Step 2: Replace standalone function names with Math. prefixed versions
    func = func
      .replace(/\^/g, '**')
      .replace(/\bsin\b/g, 'Math.sin')
      .replace(/\bcos\b/g, 'Math.cos')
      .replace(/\btan\b/g, 'Math.tan')
      .replace(/\blog\b/g, 'Math.log')
      .replace(/\bsqrt\b/g, 'Math.sqrt')
      .replace(/\babs\b/g, 'Math.abs')
      .replace(/\bexp\b/g, 'Math.exp')
      .replace(/\bpow\b/g, 'Math.pow')
      .replace(/\bpi\b/g, 'Math.PI')
      .replace(/\be\b/g, 'Math.E');
    
    // Step 3: Restore the protected Math functions
    Object.keys(mathFunctionMap).forEach(placeholder => {
      func = func.replace(placeholder, mathFunctionMap[placeholder]);
    });
    
    return (y: number) => {
      try {
        const result = safeMathEval(func, y, 'y');
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new Error(`Invalid function expression: ${errorMessage}`);
      }
    };
  };

  const calculateLinearRegression = (points: [number, number][]): { slope: number; intercept: number } => {
    const n = points.length;
    const sumX = points.reduce((sum, [x]) => sum + x, 0);
    const sumY = points.reduce((sum, [, y]) => sum + y, 0);
    const sumXY = points.reduce((sum, [x, y]) => sum + x * y, 0);
    const sumXX = points.reduce((sum, [x]) => sum + x * x, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return { slope, intercept };
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `graph-${config.title || 'untitled'}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const drawScatterGraph = (ctx: CanvasRenderingContext2D, config: ScatterGraphConfig) => {
    const datasets: ScatterDatasetConfig[] =
      (config.datasets && config.datasets.length > 0)
        ? config.datasets
        : config.points
          ? [{ points: config.points, color: config.color }]
          : [];

    const allPoints = datasets.flatMap(dataset => dataset.points || []);
    if (allPoints.length === 0) {
      throw new Error('Scatter plots require at least one point');
    }

    const xRange = config.xRange || [
      Math.min(...allPoints.map(p => p[0])) - 1,
      Math.max(...allPoints.map(p => p[0])) + 1
    ];
    const yRange = config.yRange || [
      Math.min(...allPoints.map(p => p[1])) - 1,
      Math.max(...allPoints.map(p => p[1])) + 1
    ];

    config.xRange = xRange;
    config.yRange = yRange;

    setMarkerLegendData([]);

    const legend = datasets
      .filter(dataset => dataset.label || datasets.length > 1)
      .map((dataset, index) => ({
        label: dataset.label || `Series ${index + 1}`,
        color: dataset.color || config.color || getStudybelColor(index)
      }));

    setLegendData(legend);

    datasets.forEach((dataset, datasetIndex) => {
      const pointColor = dataset.color || config.color || getStudybelColor(datasetIndex);
      ctx.fillStyle = pointColor;

      dataset.points.forEach(([x, y]) => {
        const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
        const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);

        ctx.beginPath();
        ctx.arc(canvasX, canvasY, 4, 0, 2 * Math.PI);
        ctx.fill();
      });

      const shouldDrawTrend = config.trend && datasets.length === 1 && dataset.points.length > 1;
      if (shouldDrawTrend) {
        const { slope, intercept } = calculateLinearRegression(dataset.points);

        ctx.strokeStyle = pointColor;
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 1;
        ctx.beginPath();

        const x1 = xRange[0];
        const y1 = slope * x1 + intercept;
        const x2 = xRange[1];
        const y2 = slope * x2 + intercept;

        const canvasX1 = padding + ((x1 - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
        const canvasY1 = padding + ((yRange[1] - y1) / (yRange[1] - yRange[0])) * (height - 2 * padding);
        const canvasX2 = padding + ((x2 - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
        const canvasY2 = padding + ((yRange[1] - y2) / (yRange[1] - yRange[0])) * (height - 2 * padding);

        ctx.moveTo(canvasX1, canvasY1);
        ctx.lineTo(canvasX2, canvasY2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    });
  };

  const drawBarGraph = (ctx: CanvasRenderingContext2D, config: BarGraphConfig) => {
    const labels = Object.keys(config.data);
    const values = Object.values(config.data);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values, 0);
    
    config.yRange = [minValue, maxValue * 1.1];
    
    // Clear legend data for non-function graphs
    setLegendData([]);
    setMarkerLegendData([]);
    
    const barWidth = (width - 2 * padding) / labels.length * 0.8;
    const barSpacing = (width - 2 * padding) / labels.length * 0.2;
    
    ctx.fillStyle = config.color || STUDYBEL_COLORS.green;
    
    values.forEach((value, index) => {
      const x = padding + index * (barWidth + barSpacing) + barSpacing / 2;
      const barHeight = (value / maxValue) * (height - 2 * padding);
      const y = height - padding - barHeight;
      
      ctx.fillRect(x, y, barWidth, barHeight);
      
      // Draw labels
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(labels[index], x + barWidth / 2, height - padding + 15);
      ctx.fillText(value.toString(), x + barWidth / 2, y - 5);
      ctx.fillStyle = config.color || '#3b82f6';
    });
  };

  const drawLineGraph = (ctx: CanvasRenderingContext2D, config: LineGraphConfig) => {
    const xRange = config.xRange || [
      Math.min(...config.data.map(p => p[0])),
      Math.max(...config.data.map(p => p[0]))
    ];
    const yRange = config.yRange || [
      Math.min(...config.data.map(p => p[1])),
      Math.max(...config.data.map(p => p[1]))
    ];
    
    config.xRange = xRange;
    config.yRange = yRange;
    
    // Clear legend data for non-function graphs
    setLegendData([]);
    setMarkerLegendData([]);
    
    ctx.strokeStyle = config.color || '#3b82f6';
    ctx.lineWidth = 2;
    ctx.beginPath();
    
    config.data.forEach(([x, y], index) => {
      const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
      const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
      
      if (index === 0) {
        ctx.moveTo(canvasX, canvasY);
      } else {
        ctx.lineTo(canvasX, canvasY);
      }
    });
    
    ctx.stroke();
    
    // Draw points
    ctx.fillStyle = config.color || '#3b82f6';
    config.data.forEach(([x, y]) => {
      const canvasX = padding + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (width - 2 * padding);
      const canvasY = padding + ((yRange[1] - y) / (yRange[1] - yRange[0])) * (height - 2 * padding);
      
      ctx.beginPath();
      ctx.arc(canvasX, canvasY, 3, 0, 2 * Math.PI);
      ctx.fill();
    });
  };

  const drawTimelineGraph = (ctx: CanvasRenderingContext2D, config: TimelineGraphConfig) => {
    const timelinePadding = isTimeline ? 100 : 80;
    const timelineY = height / 2;
    const timelineHeight = 50; // Increased for better visibility
    
    // Calculate time range
    let minTime = 0;
    let maxTime = 100;
    
    if (config.xRange) {
      [minTime, maxTime] = config.xRange;
    } else {
      // Calculate from events and periods
      const allTimes: number[] = [];
      config.events?.forEach(e => allTimes.push(e.time));
      config.periods?.forEach(p => {
        allTimes.push(p.start);
        allTimes.push(p.end);
      });
      config.markers?.forEach(m => allTimes.push(m.x));
      
      if (allTimes.length > 0) {
        minTime = Math.min(...allTimes);
        maxTime = Math.max(...allTimes);
        // Add some padding
        const range = maxTime - minTime;
        minTime -= range * 0.05;
        maxTime += range * 0.05;
      }
    }
    
    const timeToX = (time: number) => {
      return timelinePadding + ((time - minTime) / (maxTime - minTime)) * (width - 2 * timelinePadding);
    };
    
    // Clear background for timeline area
    ctx.fillStyle = '#f9fafb';
    ctx.fillRect(timelinePadding, timelineY - timelineHeight * 1.5, width - 2 * timelinePadding, timelineHeight * 3);
    
    // Draw periods first (above the timeline)
    if (config.periods) {
      config.periods.forEach((period, index) => {
        const startX = timeToX(period.start);
        const endX = timeToX(period.end);
        const periodWidth = endX - startX;
        const periodHeight = 30;
        const periodY = timelineY - periodHeight - 15; // Position above timeline
        
        // Draw period bar
        ctx.fillStyle = period.color || getStudybelColor(index);
        ctx.globalAlpha = 0.3;
        ctx.fillRect(startX, periodY, periodWidth, periodHeight);
        ctx.globalAlpha = 1;
        
        // Draw period border
        ctx.strokeStyle = period.color || getStudybelColor(index);
        ctx.lineWidth = 2;
        ctx.strokeRect(startX, periodY, periodWidth, periodHeight);
        
        // Draw period label
        if (period.label) {
          ctx.fillStyle = '#1f2937';
          ctx.font = isRTL ? 'bold 11px Arial' : 'bold 11px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const labelX = startX + periodWidth / 2;
          ctx.fillText(period.label, labelX, periodY + periodHeight / 2);
        }
      });
    }
    
    // Draw main timeline
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(timelinePadding, timelineY);
    ctx.lineTo(width - timelinePadding, timelineY);
    ctx.stroke();
    
    // Draw arrow at the end
    ctx.beginPath();
    ctx.moveTo(width - timelinePadding - 10, timelineY - 5);
    ctx.lineTo(width - timelinePadding, timelineY);
    ctx.lineTo(width - timelinePadding - 10, timelineY + 5);
    ctx.stroke();
    
    // Track label positions to avoid overlaps
    const labelPositions: Array<{x: number, width: number, position: 'above' | 'below'}> = [];
    
    // Draw events and markers with smart positioning
    const drawTimePoint = (time: number, label: string, type: string = 'event', showLine: boolean = true, forcePosition?: 'above' | 'below') => {
      const x = timeToX(time);
      
      // Draw vertical line
      if (showLine) {
        ctx.strokeStyle = '#9ca3af';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(x, timelineY - timelineHeight);
        ctx.lineTo(x, timelineY + timelineHeight);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      
      // Draw point
      const pointRadius = type === 'milestone' ? 8 : 6;
      const pointColor = type === 'start' ? '#10b981' : 
                        type === 'end' ? '#ef4444' : 
                        type === 'milestone' ? '#8b5cf6' : '#3b82f6';
      
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = pointColor;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, timelineY, pointRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      
      // Draw label with smart positioning
      if (label) {
        ctx.fillStyle = '#1f2937';
        ctx.font = isRTL ? 'bold 11px Arial' : 'bold 11px Arial';
        ctx.textAlign = 'center';
        
        // Wrap text if too long
        const maxWidth = 80; // Increased for better readability
        const words = label.split(' ');
        let line = '';
        let lines: string[] = [];
        
        for (let word of words) {
          const testLine = line + (line ? ' ' : '') + word;
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = testLine;
          }
        }
        lines.push(line);
        
        // Calculate label dimensions
        const lineHeight = 14;
        const totalHeight = lines.length * lineHeight + 4;
        const maxLineWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
        
        // Determine label position (above or below) to avoid overlaps
        let labelPosition: 'above' | 'below' = forcePosition || 'below';
        const minSpacing = 10;
        
        if (!forcePosition) {
          // Check for overlaps with existing labels
          const wouldOverlap = (pos: 'above' | 'below') => {
            return labelPositions.some(existing => {
              if (existing.position !== pos) return false;
              const distance = Math.abs(existing.x - x);
              return distance < (existing.width + maxLineWidth) / 2 + minSpacing;
            });
          };
          
          // Try below first, then above
          if (wouldOverlap('below') && !wouldOverlap('above')) {
            labelPosition = 'above';
          } else if (!wouldOverlap('below')) {
            labelPosition = 'below';
          } else {
            // If both would overlap, alternate based on index
            labelPosition = labelPositions.length % 2 === 0 ? 'below' : 'above';
          }
        }
        
        // Store this label's position
        labelPositions.push({x, width: maxLineWidth, position: labelPosition});
        
        // Calculate Y position based on chosen position
        const labelY = labelPosition === 'below' 
          ? timelineY + pointRadius + 10
          : timelineY - pointRadius - totalHeight - 10;
        
        // Draw label background
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fillRect(x - maxLineWidth/2 - 4, labelY, maxLineWidth + 8, totalHeight);
        
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.strokeRect(x - maxLineWidth/2 - 4, labelY, maxLineWidth + 8, totalHeight);
        
        // Draw text lines
        ctx.fillStyle = '#1f2937';
        ctx.textBaseline = 'top';
        lines.forEach((textLine, index) => {
          ctx.fillText(textLine, x, labelY + 2 + index * lineHeight);
        });
        
        // Draw connector line from point to label if needed
        if (labelPosition === 'above') {
          ctx.strokeStyle = '#d1d5db';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, timelineY - pointRadius);
          ctx.lineTo(x, labelY + totalHeight);
          ctx.stroke();
        }
      }
      
      // Draw time value at the bottom (below everything else)
      ctx.fillStyle = '#374151'; // Darker color
      ctx.font = 'bold 10px Arial'; // Bold and slightly larger
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const timeLabel = config.unit ? `${time} ${config.unit}` : time.toString();
      
      // Calculate Y position - always at bottom, below any labels
      let timeY = timelineY + pointRadius + 5;
      
      // Check if there's a label below, if so, position time label further down
      const hasLabelBelow = labelPositions.some(p => 
        p.position === 'below' && Math.abs(p.x - x) < 50
      );
      
      if (hasLabelBelow) {
        timeY = timelineY + pointRadius + 60; // Position below the milestone labels
      }
      
      // Add white background for time labels
      const metrics = ctx.measureText(timeLabel);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(x - metrics.width/2 - 2, timeY - 2, metrics.width + 4, 14);
      
      ctx.fillStyle = '#374151';
      ctx.fillText(timeLabel, x, timeY);
    };
    
    // Draw events
    if (config.events) {
      config.events.forEach(event => {
        drawTimePoint(event.time, event.label, event.type || 'event');
      });
    }
    
    // Draw markers (compatibility with old format)
    if (config.markers && !config.events) {
      config.markers.forEach(marker => {
        drawTimePoint(marker.x, marker.label, 'event');
      });
    }
    
    // Draw scale
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.font = '10px Arial';
    ctx.fillStyle = '#6b7280';
    ctx.textAlign = 'center';
    
    const numTicks = 5;
    for (let i = 0; i <= numTicks; i++) {
      const time = minTime + (maxTime - minTime) * i / numTicks;
      const x = timeToX(time);
      
      // Draw tick
      ctx.beginPath();
      ctx.moveTo(x, timelineY - 5);
      ctx.lineTo(x, timelineY + 5);
      ctx.stroke();
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
        <div className="flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span className="font-medium">Graph Rendering Error</span>
        </div>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`graph-container bg-white border border-secondary-200 rounded-lg p-4 my-4 ${isTimeline ? 'w-full' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-secondary-900" dir={isRTL ? 'rtl' : 'ltr'}>
          {config.title ? renderInlineMathSegments(config.title, 'title') : 'Graph'}
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={downloadImage}
            className="p-2 text-secondary-600 hover:text-secondary-800 hover:bg-secondary-100 rounded transition-colors"
            title="Download as PNG"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-secondary-600 hover:text-secondary-800 hover:bg-secondary-100 rounded transition-colors"
            title="Toggle fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className={`flex ${isFullscreen ? 'fixed inset-0 bg-white z-50 p-4' : 'flex-col'}`}>
        <div className={`flex justify-center ${isTimeline && !isFullscreen ? 'overflow-x-auto' : ''}`}>
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="border border-secondary-300 rounded"
          />
        </div>
        
        {/* Graph Legend */}
        {legendData.length > 0 && (legendData.length > 1 || (config.type === 'function' && config.labels)) && (
          <div className={`mt-3 px-2 ${isFullscreen ? 'max-w-4xl mx-auto' : ''}`}>
            <div className={`text-sm text-secondary-700 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
              <span className="font-medium text-secondary-800">{isRTL ? 'מקרא פונקציות: ' : 'Functions Legend: '}</span>
              <div className={`flex flex-wrap gap-3 mt-1 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                {legendData.map((item, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <div 
                      className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    {item.label ? (
                      <span
                        className="text-xs text-secondary-700"
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        {renderInlineMathSegments(item.label, `legend-${index}`)}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Markers Legend */}
      {markerLegendData.length > 0 && (
          <div className={`mt-3 px-2 ${isFullscreen ? 'max-w-4xl mx-auto' : ''}`}>
            <div className={`text-sm text-secondary-700 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
              <span className="font-medium text-secondary-800">{isRTL ? 'מקרא נקודות ציון: ' : 'Markers Legend: '}</span>
              <div className={`flex flex-col gap-2 mt-2 ${isRTL ? 'items-end' : 'items-start'}`}>
                {markerLegendData.map((marker) => (
                  <div key={marker.number} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className="flex items-center justify-center w-5 h-5 bg-white border-2 border-secondary-400 rounded-full text-xs font-bold text-secondary-700">
                      {marker.number}
                    </div>
                    <span className="text-xs text-secondary-700" dir={isRTL ? 'rtl' : 'ltr'}>
                      {marker.label ? renderInlineMathSegments(marker.label, `marker-${marker.number}`) : null}
                      {' '}
                      <span className="text-secondary-500 ml-1">
                        ({marker.type === 'x-axis' ? (isRTL ? 'ציר X' : 'X-axis') : (isRTL ? 'ציר Y' : 'Y-axis')})
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Graph Description */}
        {config.description && (
          <div className={`mt-3 px-2 ${isFullscreen ? 'max-w-4xl mx-auto' : ''}`}>
            <div className={`text-sm text-secondary-700 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
              <span className="font-medium text-secondary-800">{isRTL ? 'תובנות מהגרף: ' : 'Graph Insights: '}</span>
              <div className="inline">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    p: ({ children }) => <span className="inline">{children}</span>,
                    span: ({ className, children, ...props }) => {
                      // Handle inline math expressions (KaTeX uses span.katex)
                      if (className && className.includes('katex')) {
                        return (
                          <span 
                            className={`${className} math-inline`} 
                            style={{ 
                              direction: 'ltr',
                              unicodeBidi: 'isolate',
                              display: 'inline-block',
                              textAlign: 'left',
                              verticalAlign: 'baseline'
                            }}
                            {...props}
                          >
                            {children}
                          </span>
                        );
                      }
                      return <span className={className} {...props}>{children}</span>;
                    },
                    div: ({ className, children, ...props }) => {
                      // Handle display math expressions (KaTeX uses div.katex-display)
                      if (className && className.includes('katex-display')) {
                        return (
                          <div 
                            className={`${className} math-display my-2`}
                            style={{ 
                              direction: 'ltr',
                              unicodeBidi: 'isolate',
                              textAlign: 'center',
                              isolation: 'isolate'
                            }}
                            {...props}
                          >
                            {children}
                          </div>
                        );
                      }
                      return <div className={className} {...props}>{children}</div>;
                    },
                    strong: ({ children }) => <strong className="font-medium text-secondary-800">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    code: ({ inline, children, ...props }: any) => {
                      // Better inline detection for graph descriptions
                      const isInline = inline || (
                        typeof children === 'string' && 
                        children.length < 100 && 
                        !children.includes('\n')
                      );
                      
                      if (isInline) {
                        return (
                          <code 
                            className="inline px-1 py-0.5 bg-secondary-100 text-secondary-800 rounded text-xs font-mono"
                            style={{ display: 'inline' }}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      }
                      
                      return (
                        <code className="block p-2 bg-secondary-100 text-secondary-800 rounded text-xs font-mono" {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {preprocessMathTags(config.description)}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {isFullscreen && (
        <button
          onClick={() => setIsFullscreen(false)}
          className="fixed top-4 right-4 bg-white border border-secondary-300 rounded-lg p-2 shadow-lg z-50"
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default GraphRenderer; 
